import "../styles/AdminInterface.css"
import React, { useState, useEffect, useRef } from 'react'
import BackendAPI from "../API/BackendAPI"
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { useHistory } from 'react-router-dom';
import { TextField, Divider, Typography, TableContainer, TableHead, TableRow, Paper, TableBody, Table, TableCell } from "@material-ui/core"
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';

import Popup from "../components/Popup"
import Sidebar from "../components/Sidebar"
import cookie from 'react-cookies'
import { telechargerCSV, aujourdhui } from "../utils/csv"


// ─────────────────────────────────────────────────────────────
// Liste de référence Campus → Programme → Groupe (table groupesAccounting),
// comme la table groupesV2 de Play & Learn.
//  • L'application la propose aux étudiants à la connexion : ils ne
//    peuvent plus saisir de campus / groupe vide ou inexistant.
//  • La page Étudiants s'en sert pour ses listes Campus / Programme / Groupe.
//  • Renommer une valeur met à jour les étudiants rattachés à ce groupe.
//  • Retirer un groupe ne modifie pas les étudiants : l'application leur
//    demandera d'en choisir un autre à leur prochaine ouverture.
// ─────────────────────────────────────────────────────────────
const CHAMPS = [
  ["campus", "Campus"],
  ["programme", "Programme"],
  ["groupe", "Groupe"],
]

const MESSAGES_ERREUR = {
  doublon: "Ce groupe existe déjà (même campus, programme et groupe ; majuscules et accents ne comptent pas).",
  parametres: "Le campus, le programme et le groupe sont obligatoires (100 caractères au plus).",
  introuvable: "Ce groupe n'existe plus : la liste a été rechargée.",
  reseau: "Le serveur ne répond pas. Réessayez dans quelques instants.",
}
const messageErreur = (res) => MESSAGES_ERREUR[res?.erreur] || "L'enregistrement a échoué. Réessayez."

const AdminGroupes = () => {
  const history = useHistory()

  const user = cookie.load("user")
  const handleDisconnect = () => {
    cookie.save('user', "disconnected", {
      path: '/',
      maxAge: 60 * 60 * 1.5, //sec
    })
    history.push('/login');
  }

  if (user === null || user === undefined || user === "disconnected") history.push("/login");
  if (user !== null && user !== undefined && user.username !== undefined && !user.username.toUpperCase().includes("ADMIN")) handleDisconnect()

  const [groupes, setGroupes] = useState(null)        // données serveur
  const [lignes, setLignes] = useState([])            // copie éditable
  const [nouveau, setNouveau] = useState({ campus: "", programme: "", groupe: "" })
  const [popupGroupe, setPopupGroupe] = useState(null)
  const [enCours, setEnCours] = useState(false)
  const [erreurChargement, setErreurChargement] = useState(false)
  // Cellule en cours de saisie : un rechargement (après l'enregistrement
  // d'une autre cellule) ne doit pas effacer ce qui est en train d'être tapé.
  const editionRef = useRef(null)

  const chargementRef = useRef(0)
  const charger = async () => {
    // Seule la dernière demande compte : deux enregistrements rapprochés
    // lancent deux rechargements, l'ancien ne doit pas écraser le récent.
    const numero = ++chargementRef.current
    const liste = await BackendAPI.getGroupes()
    if (numero !== chargementRef.current) return
    if (liste === null) {        // serveur injoignable : on garde l'affichage actuel
      setErreurChargement(true)
      return
    }
    setErreurChargement(false)
    setGroupes(liste)
    setLignes((avant) => liste.map((g) => {
      const e = editionRef.current
      const local = e && e.id === g.id ? avant.find((l) => l.id === g.id) : null
      return local ? { ...g, [e.champ]: local[e.champ] } : { ...g }
    }))
  }

  useEffect(() => { charger() }, [])

  const valeursUniques = (champ) =>
    [...new Set((groupes || []).map((g) => g[champ]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)))

  const modifierLocal = (id, champ, valeur) => {
    setLignes((ls) => ls.map((l) => (l.id === id ? { ...l, [champ]: valeur } : l)))
  }

  // Enregistrement d'une cellule (sortie du champ ou Entrée)
  const enregistrer = async (ligne, champ) => {
    const origine = (groupes || []).find((g) => g.id === ligne.id)
    if (!origine) return
    const valeur = String(ligne[champ] ?? "").trim()
    if (valeur === String(origine[champ]).trim()) return
    if (valeur === "") {
      alert(MESSAGES_ERREUR.parametres)
      modifierLocal(ligne.id, champ, origine[champ])
      return
    }
    // Doublon visible tout de suite (la base ne distingue pas les majuscules) :
    // inutile de demander une confirmation qui échouerait ensuite.
    const cle = (g) => ["campus", "programme", "groupe"].map((c) => String(g[c] ?? "").trim().toLowerCase()).join("|")
    const cible = { ...origine, [champ]: valeur }
    if ((groupes || []).some((g) => g.id !== origine.id && cle(g) === cle(cible))) {
      alert(MESSAGES_ERREUR.doublon)
      modifierLocal(ligne.id, champ, origine[champ])
      return
    }
    const nb = Number(origine.nbEtudiants) || 0
    if (nb > 0 && !window.confirm(
      `Remplacer « ${origine[champ]} » par « ${valeur} » mettra aussi à jour ${nb} étudiant${nb > 1 ? "s" : ""} rattaché${nb > 1 ? "s" : ""} à ce groupe. Continuer ?`)) {
      modifierLocal(ligne.id, champ, origine[champ])
      return
    }
    const res = await BackendAPI.updateGroupe(ligne.id, champ, valeur)
    if (!res.ok) alert(messageErreur(res))
    charger()
  }

  const toucheEntree = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.target.blur()   // déclenche l'enregistrement (onBlur)
    }
  }

  const ajouter = async () => {
    if (enCours) return
    const g = {
      campus: nouveau.campus.trim(),
      programme: nouveau.programme.trim(),
      groupe: nouveau.groupe.trim(),
    }
    if (!g.campus || !g.programme || !g.groupe) {
      alert(MESSAGES_ERREUR.parametres)
      return
    }
    setEnCours(true)
    const res = await BackendAPI.createGroupe(g)
    setEnCours(false)
    if (!res.ok) {
      alert(messageErreur(res))
      return
    }
    // Campus et programme gardés : on enchaîne souvent plusieurs groupes
    // du même campus / programme.
    setNouveau({ ...g, groupe: "" })
    charger()
  }

  const handleClosePopup = async (choix) => {
    if (choix === "Retirer le groupe" && popupGroupe) {
      const res = await BackendAPI.deleteGroupe(popupGroupe.id)
      if (!res.ok) alert(messageErreur(res))
      charger()
    }
    setPopupGroupe(null)
  }

  const exportCSV = () => {
    if (!groupes || groupes.length === 0) {
      alert("Aucun groupe à exporter !")
      return
    }
    telechargerCSV(
      "Groupes - " + aujourdhui() + ".csv",
      ["Campus", "Programme", "Groupe", "Étudiants"],
      groupes.map((g) => [g.campus, g.programme, g.groupe, g.nbEtudiants])
    )
  }

  const nouveauIncomplet = !nouveau.campus.trim() || !nouveau.programme.trim() || !nouveau.groupe.trim()

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div className="body-admin">
        {/* Le titre (thème) a 20 px de marge en haut et 5 px en bas : les 15 px
          d'écart passent sur la ligne, sinon l'icône se centrait 7 px trop haut. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 15 }}>
          <Typography variant="h1" color="primary" style={{ paddingTop: 5 }}>Groupes</Typography>
          <Tooltip title="Exporter les groupes (CSV)" arrow>
            <IconButton aria-label="export groupes" style={{ padding: 10 }} onClick={exportCSV}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </div>
        <Divider />
        {popupGroupe != null ?
          <Popup
            open={true}
            reponse={"Retirer le groupe"}
            handleClose={handleClosePopup}
            text={
              <Typography>
                Souhaitez-vous retirer ce groupe de la liste ?
                <br />
                {popupGroupe.campus} - {popupGroupe.programme} - {popupGroupe.groupe}
                <br /><br />
                {Number(popupGroupe.nbEtudiants) > 0
                  ? `${popupGroupe.nbEtudiants} étudiant(s) y sont rattachés : leurs données ne changent pas, mais l'application leur demandera de choisir un autre groupe.`
                  : "Aucun étudiant n'y est rattaché."}
              </Typography>
            }
          />
          : null}

        <div style={{ margin: 10, width: "90%", maxWidth: 1100 }}>
          <Typography variant="caption" color="textSecondary" component="div">
            Cette liste est proposée aux étudiants dans l'application (Campus → Programme → Groupe) et dans la page Étudiants.
            Renommer une valeur met aussi à jour les étudiants rattachés à ce groupe.
          </Typography>
          <br />
          <TableContainer component={Paper} style={{ maxHeight: "100%" }}>
            <Table stickyHeader aria-label="groupes">
              <TableHead>
                <TableRow>
                  {CHAMPS.map(([, libelle]) => (
                    <TableCell key={libelle} align="center" style={{ width: 250 }}>{libelle}</TableCell>
                  ))}
                  <TableCell align="center" style={{ width: 100 }}>Étudiants</TableCell>
                  <TableCell align="center" style={{ width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lignes.map((l) => (
                  <TableRow key={l.id} style={{ height: 10 }}>
                    {CHAMPS.map(([champ]) => (
                      <TableCell key={champ} align="center">
                        <TextField
                          value={l[champ] ?? ""}
                          onChange={(e) => modifierLocal(l.id, champ, e.target.value)}
                          onFocus={() => { editionRef.current = { id: l.id, champ } }}
                          onBlur={() => { editionRef.current = null; enregistrer(l, champ) }}
                          onKeyDown={toucheEntree}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                    ))}
                    <TableCell align="center">{l.nbEtudiants}</TableCell>
                    <TableCell align="center">
                      <IconButton aria-label="retirer" onClick={() => setPopupGroupe(l)}>
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow key={"NOUVEAU"}>
                  {CHAMPS.map(([champ, libelle]) => (
                    <TableCell key={champ} align="center">
                      <TextField
                        label={libelle}
                        value={nouveau[champ]}
                        onChange={(e) => setNouveau({ ...nouveau, [champ]: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter() } }}
                        inputProps={{ list: "liste-" + champ }}
                        variant="outlined"
                        size="small"
                      />
                      {/* Suggestions : valeurs déjà utilisées, pour éviter « Angers » / « angers » / « Angers  » */}
                      <datalist id={"liste-" + champ}>
                        {valeursUniques(champ).map((v) => <option key={v} value={v} />)}
                      </datalist>
                    </TableCell>
                  ))}
                  <TableCell align="center"></TableCell>
                  <TableCell align="center">
                    <IconButton aria-label="ajouter" disabled={nouveauIncomplet || enCours} onClick={ajouter}>
                      <AddIcon color={nouveauIncomplet ? "disabled" : "secondary"} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {erreurChargement ?
            <Typography color="error" style={{ marginTop: 10 }}>
              La liste n'a pas pu être chargée (serveur injoignable).{" "}
              <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={charger}>Réessayer</span>
            </Typography>
            : null}
          {groupes !== null && groupes.length === 0 && !erreurChargement ?
            <Typography color="error" style={{ marginTop: 10 }}>
              La liste est vide : tant qu'elle l'est, l'application laisse les étudiants se connecter sans choisir de groupe.
            </Typography>
            : null}
        </div>
      </div>
    </div>
  );
}

export default AdminGroupes;
